import { cloneDeep, differenceWith, find, get, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { FIELD_PATHS, INCOME_TYPE, REDUX_STORE } from '@funnels/constants';
import {
  Asset,
  Company,
  CreateIncomeAction,
  DeleteIncomeAction,
  FetchIncomesAction,
  Income,
  ReduxStoreData,
  ServerIncomeCreate,
  ServerIncomeUpdate,
  StoreApplicant,
  StoreEmployment,
  UpdateIncomeAction,
  UpsertIncomesAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import {
  getIncomeId,
  removeClientSideOwnershipReference,
  waitForAuth,
  waitForResourcesToBeCreated,
  waitForResourcesToBeFree,
} from '../_common';
import {
  createIncome,
  createIncomeFail,
  createIncomeSuccess,
  deleteIncomeFail,
  deleteIncomeSuccess,
  fetchIncomesFail,
  fetchIncomesSuccess,
  setIncomeError,
  updateIncome,
  updateIncomeAbort,
  updateIncomeFail,
  updateIncomeSuccess,
  upsertIncomesFail,
  upsertIncomesSuccess,
} from './actions';
import IncomeAPI from './api';
import { fixCompany, fixEmployment, fixRentalIncome } from './utils';

export function* createIncomeWorker(action: CreateIncomeAction): any {
  try {
    yield waitForAuth();
    const { applicationId } = action;
    const { clientId, ...income } = action.income;
    const serverIncome = omit(income, ['employmentClientId', 'assetClientId', 'companyClientId']) as ServerIncomeCreate;

    const response = yield call(IncomeAPI.create, applicationId, serverIncome);

    yield put(createIncomeSuccess(clientId, response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      income: { clientId },
    } = action;
    yield all([put(createIncomeFail(clientId)), put(setIncomeError(e.response?.data || e.message))]);
  }
}

export function* deleteIncomeWorker(action: DeleteIncomeAction): any {
  try {
    yield waitForAuth();
    const { applicationId, clientId, id } = action;
    const incomeId = yield getIncomeId(clientId, id);

    yield waitForResourcesToBeFree(['employments']);

    yield call(IncomeAPI.del, applicationId, incomeId);

    yield put(deleteIncomeSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield all([put(deleteIncomeFail(clientId)), put(setIncomeError(e.response?.data || e.message))]);
  }
}

export function* fetchIncomesWorker(action: FetchIncomesAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;
    const response = yield call(IncomeAPI.fetch, applicationId);
    const incomes = response.data
      // filter out v1 SE incomes
      .filter(
        (income: Income) =>
          get({ incomes: [income] }, FIELD_PATHS.INCOME_TYPE(0)) !== INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME
      )
      .map((income: any) => omit(income, ['meta', 'updatedAt']))
      .sort((i1: any, i2: any) => {
        const amount1 = get({ incomes: [i1] }, FIELD_PATHS.INCOME_AMOUNT(0), 0);
        const amount2 = get({ incomes: [i2] }, FIELD_PATHS.INCOME_AMOUNT(0), 0);

        return amount2 - amount1;
      });

    yield put(fetchIncomesSuccess(incomes));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(fetchIncomesFail()), put(setIncomeError(e.response?.data || e.message))]);
  }
}

export function* updateIncomeWorker(action: UpdateIncomeAction): any {
  try {
    yield waitForAuth();
    const { applicationId } = action;
    const incomeId = yield getIncomeId(action.income.clientId, action.income.id);

    const income = omit(action.income, [
      'clientId',
      'createdAt',
      'isDeleted',
      'isDeidentified',
      'applicationId',
      'id',
      'employmentClientId',
      'assetClientId',
      'companyClientId',
    ]) as ServerIncomeUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!incomeId) {
      yield all([put(updateIncomeAbort()), put(createIncome(applicationId, action.income))]);
    } else {
      yield call(IncomeAPI.update, applicationId, incomeId, omit(income, 'applicationId'));

      yield put(updateIncomeSuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      income: { clientId },
    } = action;
    yield all([put(updateIncomeFail(clientId)), put(setIncomeError(e.response?.data || e.message))]);
  }
}

export function* upsertIncomesWorker(action: UpsertIncomesAction): any {
  try {
    yield waitForAuth();
    let reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);
    let reduxStoreAssets = yield select((state: ReduxStoreData) => state.assets.data);
    let reduxStoreEmployments = yield select((state: ReduxStoreData) => state.employments.data);
    let reduxStoreCompanies = yield select((state: ReduxStoreData) => state.companies.data);

    yield waitForResourcesToBeCreated({
      applicants: reduxStoreApplicants.map((a: StoreApplicant) => a.clientId),
      assets: reduxStoreAssets.map((a: Asset) => a.clientId),
      employments: reduxStoreEmployments.map((e: StoreEmployment) => e.clientId),
      companies: reduxStoreCompanies.map((c: Company) => c.clientId),
    });

    yield waitForResourcesToBeFree(['employments', 'companies', 'assets']);

    reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);
    reduxStoreAssets = yield select((state: ReduxStoreData) => state.assets.data);
    reduxStoreEmployments = yield select((state: ReduxStoreData) => state.employments.data);
    reduxStoreCompanies = yield select((state: ReduxStoreData) => state.companies.data);

    const { applicationId, incomes } = action;

    const { incomes: reduxStoreIncomes }: { incomes: Income[] } = yield select((state: ReduxStoreData) => ({
      incomes: state.incomes.data,
    }));

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedIncomes = differenceWith(incomes, reduxStoreIncomes, ({ id: id1, ...rest1 }, { id: id2, ...rest2 }) =>
      isEqual(omit(rest1, 'assetId', 'companyId', 'employmentId'), omit(rest2, 'assetId', 'companyId', 'employmentId'))
    );

    yield all(
      cloneDeep(changedIncomes).map((clientIncome) => {
        fixEmployment(clientIncome, reduxStoreEmployments);
        fixRentalIncome(clientIncome, reduxStoreAssets);
        fixCompany(clientIncome, reduxStoreCompanies);

        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const reduxStoreIncome = find(reduxStoreIncomes, { clientId: clientIncome.clientId }) as Income;
        const income = removeClientSideOwnershipReference(clientIncome, reduxStoreApplicants, false) as Income;

        if (!reduxStoreIncome) {
          // No income
          return put(createIncome(applicationId, income));
        } else if (!reduxStoreIncome.id) {
          // income but no id, therefore create has been fired but success hasn't returned
          return put(updateIncome(applicationId, income));
        }

        // Income is in the store and has an id
        return put(updateIncome(applicationId, { id: reduxStoreIncome.id, ...income }));
      })
    );

    yield put(upsertIncomesSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(upsertIncomesFail()), put(setIncomeError(e.response?.data || e.message))]);
  }
}

export function* incomesWatcher() {
  yield takeEvery(REDUX_STORE.INCOMES.ACTION_TYPES.CREATE_INCOME, createIncomeWorker);
  yield takeEvery(REDUX_STORE.INCOMES.ACTION_TYPES.DELETE_INCOME, deleteIncomeWorker);
  yield takeEvery(REDUX_STORE.INCOMES.ACTION_TYPES.FETCH_INCOMES, fetchIncomesWorker);
  yield takeEvery(REDUX_STORE.INCOMES.ACTION_TYPES.UPDATE_INCOME, updateIncomeWorker);
  yield takeEvery(REDUX_STORE.INCOMES.ACTION_TYPES.UPSERT_INCOMES, upsertIncomesWorker);
}

export default incomesWatcher;
